import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { AdminTenant } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';

interface TenantCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (tenant: AdminTenant) => void;
}

/**
 * `POST /v1/admin/tenants` (plan D-M). A `409 conflict` on a duplicate code
 * is rendered as a specific, formal-register error on the code field —
 * never a generic toast — per the wave brief. A `403 access_denied`
 * (e.g. a stale session that lost the admin role mid-flow) replaces the
 * form with the designed access-denied state: hiding the trigger button is
 * a convenience, never the security boundary.
 */
export function TenantCreateDialog({ open, onClose, onCreated }: TenantCreateDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [codeError, setCodeError] = useState<string>();
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function resetAndClose() {
    setCode('');
    setName('');
    setCodeError(undefined);
    setAccessDenied(false);
    onClose();
  }

  async function handleSubmit() {
    setCodeError(undefined);
    setSubmitting(true);
    try {
      const tenant = await payApiClient.createAdminTenant({ code: code.trim(), name: name.trim() });
      notify(translate('pay.tenants.create.success'), { type: 'success' });
      setCode('');
      setName('');
      onCreated(tenant);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'conflict') {
        setCodeError(translate('pay.tenants.create.conflictError'));
      } else if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = code.trim().length > 0 && name.trim().length > 0 && !submitting;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.tenants.create.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label={translate('pay.tenants.create.fieldCode')}
              helperText={codeError ?? translate('pay.tenants.create.fieldCodeHelp')}
              error={Boolean(codeError)}
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setCodeError(undefined);
              }}
              fullWidth
            />
            <TextField
              size="small"
              label={translate('pay.tenants.create.fieldName')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{translate('pay.tenants.create.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {translate('pay.tenants.create.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
